/**
 * Live Chat Service - Default Implementation
 *
 * Manages chat connection lifecycle:
 * - Loads initial messages via RPC
 * - Listens for new messages via SSE
 * - Sends messages via RPC
 * - Handles reconnection on event change
 */

import { inject, effect } from "../../di";
import type {
  IWritableLiveChatState,
  ILiveChatService,
  IChatMessage,
  IChatAttachment,
  INewChatAttachment,
} from "./types";
import type { IRpcService } from "../rpc/types";
import type { ISseService } from "../sse/types";
import type { IAuthProvider } from "../auth/types";
import type { IWritableNotificationsState } from "../notifications/types";

const INITIAL_LOAD_LIMIT = 50;
const OLDER_MESSAGES_LIMIT = 50;
const CHAT_USERNAME_KEY = "wow_chat_username";
const LAST_SEEN_KEY_PREFIX = "wow_chat_last_seen_";

export class LiveChatService implements ILiveChatService {
  @inject<IWritableLiveChatState>("writableLiveChatState")
  private chatState!: IWritableLiveChatState;

  @inject<IRpcService>("rpcService")
  private rpcService!: IRpcService;

  @inject<ISseService>("sseService")
  private sseService!: ISseService;

  @inject<IAuthProvider>("authProvider")
  private authProvider!: IAuthProvider;

  @inject<IWritableNotificationsState>("writableNotificationsState")
  private notificationsState!: IWritableNotificationsState;

  private unsubscribeSse: (() => void) | null = null;
  private notificationsFilterCleanup: (() => void) | null = null;
  private banUnsubscribe: (() => void) | null = null;
  private deletedUnsubscribe: (() => void) | null = null;

  async connect(liveEventId: string, creatorInfluencerId: string): Promise<void> {
    this.disconnect();

    this.chatState.currentEventId.set(liveEventId);
    this.chatState.isBanned.set(false);
    this.chatState.currentCreatorId.set(creatorInfluencerId);
    this.chatState.connectionStatus.set("loading");
    this.chatState.errorMessage.set("");
    this.chatState.hasMore.set(false);

    const chatNotification = this.notificationsState.chatNotifications.get().find((c) => c.chatId === liveEventId);
    this.chatState.unreadCount.set(chatNotification?.unreadCount ?? 0);

    this.setupNotificationsFilter();
    // Setup SSE listener FIRST to catch messages during RPC load
    this.setupSseListener(liveEventId);
    this.setupBanListener();
    this.setupDeletedListener(liveEventId);

    try {
      const result = await this.rpcService.call<{ messages: IChatMessage[]; hasMore: boolean; isBanned: boolean }>(
        "get-live-chat-messages",
        {
          liveEventId,
          limit: String(INITIAL_LOAD_LIMIT),
        },
      );

      const loadedMessages: IChatMessage[] = result.messages.map((m) => ({
        id: m.id,
        userId: m.userId,
        userName: m.userName,
        userAvatarUrl: m.userAvatarUrl,
        content: m.content,
        sentAt: m.sentAt,
        attachments: m.attachments ?? [],
        authorIsProtected: m.authorIsProtected ?? false,
      }));

      // Merge loaded messages with any received via SSE during loading
      this.mergeInitialMessages(loadedMessages);
      this.chatState.hasMore.set(result.hasMore);
      this.chatState.isBanned.set(result.isBanned ?? false);
      this.chatState.connectionStatus.set("connected");

      // Server-derived unreadCount (from notification state) is the source of truth on connect.
      // Only override here to mark-read if the chat is already being viewed.
      if (this.chatState.isViewing.get()) {
        this.markAsRead();
      }
    } catch (error) {
      this.chatState.connectionStatus.set("error");
      this.chatState.errorMessage.set(error instanceof Error ? error.message : "Failed to load chat");
    }
  }

  disconnect(): void {
    this.removeSseListener();
    if (this.banUnsubscribe) {
      this.banUnsubscribe();
      this.banUnsubscribe = null;
    }
    if (this.deletedUnsubscribe) {
      this.deletedUnsubscribe();
      this.deletedUnsubscribe = null;
    }
    if (this.notificationsFilterCleanup) {
      this.notificationsFilterCleanup();
      this.notificationsFilterCleanup = null;
    }
    this.chatState.messages.set([]);
    this.chatState.connectionStatus.set("idle");
    this.chatState.currentEventId.set(null);
    this.chatState.errorMessage.set("");
    this.chatState.isSending.set(false);
    this.chatState.unreadCount.set(0);
    this.chatState.hasMore.set(false);
    this.chatState.loadingOlder.set(false);
    this.chatState.isBanned.set(false);
    this.chatState.currentCreatorId.set(null);
  }

  async loadOlderMessages(): Promise<void> {
    if (this.chatState.loadingOlder.get() || !this.chatState.hasMore.get()) return;

    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return;

    const messages = this.chatState.messages.get();
    if (messages.length === 0) return;

    const oldestMessage = messages[0]!;
    this.chatState.loadingOlder.set(true);

    try {
      const result = await this.rpcService.call<{ messages: IChatMessage[]; hasMore: boolean }>(
        "get-live-chat-messages",
        {
          liveEventId: eventId,
          limit: String(OLDER_MESSAGES_LIMIT),
          before: oldestMessage.id,
        },
      );

      const olderMessages: IChatMessage[] = result.messages.map((m) => ({
        id: m.id,
        userId: m.userId,
        userName: m.userName,
        userAvatarUrl: m.userAvatarUrl,
        content: m.content,
        sentAt: m.sentAt,
        attachments: m.attachments ?? [],
        authorIsProtected: m.authorIsProtected ?? false,
      }));

      const currentMessages = this.chatState.messages.get();
      const existingIds = new Set(currentMessages.map((m) => m.id));
      const newMessages = olderMessages.filter((m) => !existingIds.has(m.id));
      this.chatState.messages.set([...newMessages, ...currentMessages]);
      this.chatState.hasMore.set(result.hasMore);
    } catch (error) {
      console.error("[LiveChat] Failed to load older messages:", error);
    } finally {
      this.chatState.loadingOlder.set(false);
    }
  }

  async sendMessage(content: string, notifyByEmail?: boolean, attachments?: INewChatAttachment[]): Promise<boolean> {
    if (this.chatState.isBanned.get()) return false;

    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return false;

    const trimmedContent = content.trim();
    const hasAttachments = (attachments?.length ?? 0) > 0;
    if ((!trimmedContent && !hasAttachments) || trimmedContent.length > 1000) return false;

    this.chatState.isSending.set(true);

    try {
      const storedUsername = localStorage.getItem(CHAT_USERNAME_KEY);
      const result = await this.rpcService.call<{ success: boolean }>("send-live-chat-message", {
        liveEventId: eventId,
        content: trimmedContent,
        ...(storedUsername && { userName: storedUsername }),
        ...(notifyByEmail && { notifyByEmail: true }),
        ...(hasAttachments && { attachments }),
      });

      return result.success;
    } catch (error) {
      console.error("[LiveChat] Failed to send message:", error);
      return false;
    } finally {
      this.chatState.isSending.set(false);
    }
  }

  markAsRead(): void {
    const messages = this.chatState.messages.get();
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      this.setLastSeenMessageId(lastMessage.id);
    }

    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return;

    const previousUnread = this.chatState.unreadCount.get();
    this.chatState.unreadCount.set(0);

    const chatNotifications = this.notificationsState.chatNotifications.get();
    const filtered = chatNotifications.filter((c) => c.chatId !== eventId);
    if (filtered.length !== chatNotifications.length) {
      this.notificationsState.chatNotifications.set(filtered);
    }

    if (previousUnread > 0) {
      const currentTotal = this.notificationsState.unreadCount.get();
      this.notificationsState.unreadCount.set(Math.max(0, currentTotal - previousUnread));
    }

    this.rpcService.call("mark-live-event-chat-read", { live_event_id: eventId }).catch(() => {});
  }

  setViewing(viewing: boolean): void {
    this.chatState.isViewing.set(viewing);
    if (viewing) {
      this.markAsRead();
    }
  }

  dispose(): void {
    this.disconnect();
    this.chatState.isViewing.set(false);
  }

  private setupNotificationsFilter(): void {
    this.notificationsFilterCleanup = effect(() => {
      if (!this.chatState.isViewing.get()) return;
      const id = this.chatState.currentEventId.get();
      if (!id) return;
      const items = this.notificationsState.chatNotifications.get();
      const match = items.find((c) => c.chatId === id);
      if (!match) return;

      this.notificationsState.chatNotifications.set(items.filter((c) => c.chatId !== id));
      if (match.unreadCount > 0) {
        const currentTotal = this.notificationsState.unreadCount.get();
        this.notificationsState.unreadCount.set(Math.max(0, currentTotal - match.unreadCount));
      }
    });
  }

  private mergeInitialMessages(loadedMessages: IChatMessage[]): void {
    const currentMessages = this.chatState.messages.get();

    if (currentMessages.length === 0) {
      this.chatState.messages.set(loadedMessages);
      return;
    }

    const existingIds = new Set(currentMessages.map((m) => m.id));
    const newFromLoad = loadedMessages.filter((m) => !existingIds.has(m.id));

    // Combine: loaded (historical) + SSE messages (newer)
    const merged = [...newFromLoad, ...currentMessages];
    merged.sort((a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime());

    this.chatState.messages.set(merged);
  }

  private setupSseListener(liveEventId: string): void {
    this.unsubscribeSse = this.sseService.on<{
      messageId: string;
      liveEventId: string;
      userId: string;
      userName: string;
      userAvatarUrl: string | null;
      content: string;
      sentAt: string;
      attachments?: IChatAttachment[];
    }>("live-chat-message", (data) => {
      // Only process messages for current event
      if (data.liveEventId !== liveEventId) return;

      const newMessage: IChatMessage = {
        id: data.messageId,
        userId: data.userId,
        userName: data.userName,
        userAvatarUrl: data.userAvatarUrl,
        content: data.content,
        sentAt: data.sentAt,
        attachments: data.attachments ?? [],
        authorIsProtected: false,
      };

      const currentMessages = this.chatState.messages.get();

      // Deduplicate: check if message already exists
      if (currentMessages.some((m) => m.id === newMessage.id)) return;

      this.chatState.messages.set([...currentMessages, newMessage]);

      const currentUser = this.authProvider.getCurrentUser();
      const isOwnMessage = currentUser && currentUser.id === data.userId;
      if (isOwnMessage) {
        this.setLastSeenMessageId(newMessage.id);
      } else if (!this.chatState.isViewing.get()) {
        this.chatState.unreadCount.set(this.chatState.unreadCount.get() + 1);
      }
      if (this.chatState.isViewing.get()) {
        this.setLastSeenMessageId(newMessage.id);
      }
    });
  }

  private removeSseListener(): void {
    if (this.unsubscribeSse) {
      this.unsubscribeSse();
      this.unsubscribeSse = null;
    }
  }

  private setupBanListener(): void {
    this.banUnsubscribe = this.sseService.on<{ creatorInfluencerId: string; userId: string }>("chat-banned", (data) => {
      const creatorId = this.chatState.currentCreatorId.get();
      if (!creatorId || data.creatorInfluencerId !== creatorId) return;
      const currentUser = this.authProvider.getCurrentUser();
      if (!currentUser || currentUser.id !== data.userId) return;
      this.chatState.isBanned.set(true);
    });
  }

  private setupDeletedListener(liveEventId: string): void {
    this.deletedUnsubscribe = this.sseService.on<{
      scope: "live" | "community";
      scopeId: string;
      messageId: string;
    }>("chat-message-deleted", (data) => {
      if (data.scope !== "live" || data.scopeId !== liveEventId) return;
      const current = this.chatState.messages.get();
      const next = current.filter((m) => m.id !== data.messageId);
      if (next.length !== current.length) {
        this.chatState.messages.set(next);
      }
    });
  }

  private getLastSeenMessageId(): string | null {
    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return null;
    return localStorage.getItem(`${LAST_SEEN_KEY_PREFIX}${eventId}`);
  }

  private setLastSeenMessageId(messageId: string): void {
    const eventId = this.chatState.currentEventId.get();
    if (!eventId) return;
    localStorage.setItem(`${LAST_SEEN_KEY_PREFIX}${eventId}`, messageId);
  }

  private updateUnreadCount(): void {
    if (this.chatState.isViewing.get()) {
      this.markAsRead();
      return;
    }
    const messages = this.chatState.messages.get();
    const lastSeenId = this.getLastSeenMessageId();
    if (!lastSeenId) {
      this.chatState.unreadCount.set(messages.length);
      return;
    }
    const lastSeenIndex = messages.findIndex((m) => m.id === lastSeenId);
    if (lastSeenIndex === -1) {
      this.chatState.unreadCount.set(messages.length);
      return;
    }
    this.chatState.unreadCount.set(messages.length - 1 - lastSeenIndex);
  }
}
